import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSEO } from '@/hooks/useSEO';
import { supabase } from '@/integrations/supabase/client';
import confetti from 'canvas-confetti';
import {
  Satellite, Globe, Zap, Clock, ChevronDown, ChevronUp, X, Menu,
  ArrowRight, Check, Shield, BarChart3, Mountain, Droplets, Building2,
  TreePine, Send, Linkedin, Mail, MapPin
} from 'lucide-react';

// ─── COUNTRIES LIST ───
const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Angola","Argentina","Australia","Austria","Azerbaijan",
  "Bahrain","Bangladesh","Belgium","Bolivia","Botswana","Brazil","Burkina Faso","Cameroon",
  "Canada","Chad","Chile","China","Colombia","Congo (DRC)","Côte d'Ivoire","Czech Republic",
  "Denmark","Ecuador","Egypt","Ethiopia","Finland","France","Gabon","Germany","Ghana","Greece",
  "Guatemala","Guinea","Honduras","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy",
  "Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kuwait","Kyrgyzstan","Lebanon","Libya",
  "Madagascar","Malawi","Malaysia","Mali","Mauritania","Mexico","Mongolia","Morocco","Mozambique",
  "Myanmar","Namibia","Netherlands","New Zealand","Niger","Nigeria","Norway","Oman","Pakistan",
  "Panama","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia",
  "Rwanda","Saudi Arabia","Senegal","Sierra Leone","Singapore","South Africa","South Korea",
  "Spain","Sri Lanka","Sudan","Sweden","Switzerland","Tanzania","Thailand","Tunisia","Turkey",
  "UAE","Uganda","UK","Ukraine","USA","Uruguay","Uzbekistan","Venezuela","Vietnam","Zambia","Zimbabwe"
];

// ─── ANIMATED COUNTER ───
const AnimatedCounter = ({ end, suffix = '', prefix = '' }: { end: number; suffix?: string; prefix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const duration = 2000;
        const startTime = Date.now();
        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * end));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return <div ref={ref} className="text-4xl md:text-5xl font-bold text-sovereign-gold">{prefix}{count.toLocaleString()}{suffix}</div>;
};

// ─── FAQ ITEM ───
const FaqItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-sovereign-navy/10">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left gap-4">
        <span className="font-semibold text-sovereign-navy text-lg">{q}</span>
        {open ? <ChevronUp className="h-5 w-5 text-sovereign-gold flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-sovereign-gold flex-shrink-0" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <p className="pb-5 text-sovereign-navy/70 leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── USE CASE CARD ───
const UseCaseCard = ({ icon, title, points }: { icon: string; title: string; points: string[] }) => (
  <div className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-sovereign-navy/5">
    <div className="text-5xl mb-4">{icon}</div>
    <h3 className="text-xl font-bold text-sovereign-navy mb-4">{title}</h3>
    <ul className="space-y-2">
      {points.map((p, i) => (
        <li key={i} className="flex items-start gap-2 text-sovereign-navy/70 text-sm">
          <Check className="h-4 w-4 text-sovereign-gold mt-0.5 flex-shrink-0" />
          <span>{p}</span>
        </li>
      ))}
    </ul>
  </div>
);

// ─── PRICING CARD ───
const PricingCard = ({ title, desc, features, cta, highlighted = false }: { title: string; desc: string; features: string[]; cta: string; highlighted?: boolean }) => (
  <div className={`rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 ${highlighted ? 'bg-sovereign-navy text-white ring-4 ring-sovereign-gold shadow-2xl relative' : 'bg-white border border-sovereign-navy/10 shadow-md'}`}>
    {highlighted && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-sovereign-gold text-sovereign-navy text-xs font-bold px-4 py-1 rounded-full">Most Popular</div>}
    <h3 className={`text-xl font-bold mb-2 ${highlighted ? 'text-sovereign-gold' : 'text-sovereign-navy'}`}>{title}</h3>
    <p className={`text-sm mb-6 ${highlighted ? 'text-white/70' : 'text-sovereign-navy/60'}`}>{desc}</p>
    <ul className="space-y-3 mb-8">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${highlighted ? 'text-sovereign-gold' : 'text-sovereign-gold'}`} />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <a href="#analyze-my-zone" className={`block text-center py-3 px-6 rounded-lg font-semibold transition-all ${highlighted ? 'bg-sovereign-gold text-sovereign-navy hover:bg-sovereign-gold/90' : 'border-2 border-sovereign-navy text-sovereign-navy hover:bg-sovereign-navy hover:text-white'}`}>
      {cta}
    </a>
  </div>
);

// ═══════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════
const SovereignScout = () => {
  useSEO({
    title: 'Sovereign Scout | Satellite Intelligence for Natural Resource Exploration — SOCRAT AI Platform',
    description: 'Sovereign Scout delivers AI-powered satellite analysis for mining, oil & gas, agriculture and real estate. Get your zone scanned and receive a full intelligence report in 48 hours.',
    keywords: 'satellite natural resource exploration, mineral exploration from satellite, satellite soil analysis service, remote sensing mining exploration, AI satellite agriculture yield prediction, oil and gas exploration satellite, satellite land analysis report, geospatial intelligence service, natural resource detection from space, 48 hour satellite scan report',
    ogTitle: 'Sovereign Scout — Your Territory. Decoded from Space.',
    ogDescription: 'AI-powered satellite analysis for mining, oil & gas, agriculture and real estate. Full intelligence report in 48 hours.',
    canonical: 'https://quantumbox.ai/sovereign-scout'
  });

  const [mobileNav, setMobileNav] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formEmail, setFormEmail] = useState('');

  const [form, setForm] = useState({
    full_name: '', company: '', email: '', phone: '', country: '',
    zone_location: '', zone_size: '',
    analysis_types: [] as string[], report_type: '', project_urgency: 'standard',
    additional_context: '', nda_required: false, how_found_us: ''
  });

  const updateField = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));
  const toggleAnalysisType = (type: string) => {
    setForm(f => ({
      ...f,
      analysis_types: f.analysis_types.includes(type)
        ? f.analysis_types.filter(t => t !== type)
        : [...f.analysis_types, type]
    }));
  };

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileNav(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error } = await supabase.from('zone_analysis_requests').insert({
        full_name: form.full_name,
        company: form.company || null,
        email: form.email,
        phone: form.phone || null,
        country: form.country,
        zone_location: form.zone_location,
        zone_size: form.zone_size,
        analysis_types: form.analysis_types,
        report_type: form.report_type,
        project_urgency: form.project_urgency,
        additional_context: form.additional_context || null,
        nda_required: form.nda_required,
        how_found_us: form.how_found_us || null
      });
      if (error) throw error;
      setFormEmail(form.email);
      setSubmitted(true);
      confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
    } catch (err) {
      console.error('Submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const navLinks = [
    { label: 'Solutions', id: 'solutions' },
    { label: 'How It Works', id: 'how-it-works' },
    { label: 'Use Cases', id: 'use-cases' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Contact', id: 'analyze-my-zone' },
  ];

  const step1Valid = form.full_name && form.email && form.country && form.zone_location && form.zone_size;
  const step2Valid = form.analysis_types.length > 0 && form.report_type;

  return (
    <div className="font-sans" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        "name": "Sovereign Scout",
        "description": "AI-powered satellite intelligence for natural resource exploration, mining, oil & gas, and agriculture.",
        "url": "https://quantumbox.ai/sovereign-scout",
        "areaServed": "Global",
        "serviceType": ["Satellite Analysis", "Geospatial Intelligence", "Natural Resource Exploration"],
        "brand": { "@type": "Brand", "name": "SOCRAT" }
      })}} />

      {/* ─── NAV ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <button onClick={() => scrollTo('hero')} className="flex items-center gap-2">
            <Satellite className="h-6 w-6 text-sovereign-navy" />
            <span className="text-lg font-extrabold tracking-widest text-sovereign-navy">SOVEREIGN SCOUT</span>
          </button>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(l => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="text-sm font-medium text-sovereign-navy/70 hover:text-sovereign-navy transition-colors">{l.label}</button>
            ))}
          </div>
          <div className="hidden md:block">
            <button onClick={() => scrollTo('analyze-my-zone')} className="bg-sovereign-navy text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-sovereign-navy/90 transition-colors border-2 border-sovereign-gold">
              Get Free Zone Scan →
            </button>
          </div>
          <button className="md:hidden" onClick={() => setMobileNav(!mobileNav)}>{mobileNav ? <X /> : <Menu />}</button>
        </div>
        {mobileNav && (
          <div className="md:hidden bg-white border-t px-4 py-4 space-y-3">
            {navLinks.map(l => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="block w-full text-left text-sovereign-navy font-medium py-2">{l.label}</button>
            ))}
            <button onClick={() => scrollTo('analyze-my-zone')} className="w-full bg-sovereign-navy text-white py-3 rounded-lg font-semibold border-2 border-sovereign-gold">Get Free Zone Scan →</button>
          </div>
        )}
      </nav>

      {/* ─── HERO ─── */}
      <section id="hero" className="relative min-h-screen flex items-center pt-16 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #0f2847 50%, #1B3A6B 100%)' }}>
        {/* Animated dots grid */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="1.5" fill="#C9A84C" className="animate-pulse" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>
        {/* Orbit ring */}
        <div className="absolute top-20 right-10 md:right-20 w-48 h-48 md:w-72 md:h-72 opacity-20">
          <div className="w-full h-full rounded-full border-2 border-sovereign-gold animate-spin" style={{ animationDuration: '20s' }}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-sovereign-gold rounded-full" />
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
              Satellite Intelligence for{' '}
              <span className="text-sovereign-gold">Natural Resource Exploration</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-4 font-medium">
              Mining · Oil & Gas · Agriculture · Real Estate — <span className="text-sovereign-gold">Decoded from Space in 48 Hours</span>
            </p>
            <p className="text-white/60 mb-8 max-w-2xl leading-relaxed">
              Stop spending months on costly field surveys. SOCRAT analyzes any zone on Earth using multi-source satellite data and AI deep-learning to deliver a precise intelligence report — in 48 hours. No equipment. No travel. Just decisions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <button onClick={() => scrollTo('analyze-my-zone')} className="bg-sovereign-navy text-white px-8 py-4 rounded-lg font-bold text-lg border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-all flex items-center justify-center gap-2">
                Analyze My Zone Now <ArrowRight className="h-5 w-5" />
              </button>
              <button onClick={() => setShowModal(true)} className="border-2 border-white/30 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-all">
                See a Sample Report
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { icon: <Satellite className="h-6 w-6 text-sovereign-gold" />, text: 'Multi-Source Satellite Constellation' },
                { icon: <Zap className="h-6 w-6 text-sovereign-gold" />, text: 'AI Deep-Learning Engine' },
                { icon: <Clock className="h-6 w-6 text-sovereign-gold" />, text: 'Results in 48H' }
              ].map((t, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                  {t.icon}
                  <span className="text-white/80 text-sm font-medium">{t.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── PROBLEM vs SOLUTION ─── */}
      <section id="solutions" className="py-20 md:py-28" style={{ background: '#F4F6FA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-sovereign-navy text-center mb-16">
            Why Choose <span className="text-sovereign-gold">SOCRAT</span>?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 md:gap-16">
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <h3 className="text-xl font-bold text-red-600 mb-6">✕ The Old Way</h3>
              <ul className="space-y-4">
                {[
                  'Weeks or months of on-the-ground surveys',
                  'Millions spent on exploratory drilling in the dark',
                  'Data hidden behind complex GIS software',
                  'No results until you\'ve already committed capital'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sovereign-navy/70">
                    <X className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-md border-2 border-sovereign-gold/30">
              <h3 className="text-xl font-bold text-sovereign-gold mb-6">✓ The SOCRAT Way</h3>
              <ul className="space-y-4">
                {[
                  'Full zone analysis delivered in 48 hours',
                  'Identify high-potential zones before spending on drilling',
                  'Executive-ready intelligence reports, no software needed',
                  'Validate before you invest — reduce risk by up to 80%'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sovereign-navy/80">
                    <Check className="h-5 w-5 text-sovereign-gold mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section id="how-it-works" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-sovereign-navy text-center mb-16">
            How <span className="text-sovereign-gold">SOCRAT</span> Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-16 left-1/6 right-1/6 h-0.5 border-t-2 border-dashed border-sovereign-gold/30" style={{ left: '16%', right: '16%' }} />
            {[
              { step: '01', title: 'SUBMIT', desc: 'Define your zone and select analysis type. Takes 3 minutes online.', icon: <Send className="h-8 w-8" /> },
              { step: '02', title: 'ANALYZE', desc: 'SOCRAT processes your zone across our satellite constellation using multi-spectral, thermal, and deep-learning models.', icon: <Satellite className="h-8 w-8" /> },
              { step: '03', title: 'RECEIVE', desc: 'Your executive intelligence report is delivered in 48 hours — ready for board presentations or investment decisions.', icon: <BarChart3 className="h-8 w-8" /> }
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }} className="text-center relative z-10">
                <div className="w-20 h-20 mx-auto bg-sovereign-navy rounded-2xl flex items-center justify-center text-sovereign-gold mb-6 shadow-lg">
                  {s.icon}
                </div>
                <div className="text-sovereign-gold font-extrabold text-sm mb-2">STEP {s.step}</div>
                <h3 className="text-xl font-bold text-sovereign-navy mb-3">{s.title}</h3>
                <p className="text-sovereign-navy/60 max-w-xs mx-auto">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <button onClick={() => scrollTo('analyze-my-zone')} className="bg-sovereign-navy text-white px-8 py-4 rounded-lg font-bold border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-all inline-flex items-center gap-2">
              Start Your Analysis <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* ─── USE CASES ─── */}
      <section id="use-cases" className="py-20 md:py-28" style={{ background: '#F4F6FA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-sovereign-navy text-center mb-4">
            Satellite Analysis <span className="text-sovereign-gold">Use Cases</span>
          </h2>
          <p className="text-center text-sovereign-navy/60 mb-16 max-w-2xl mx-auto">From mineral exploration to agricultural yield prediction — SOCRAT decodes any zone on Earth.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <UseCaseCard icon="⛏️" title="Mining & Mineral Exploration" points={['Detect gold, copper, rare earth anomalies from orbit', 'Reduce exploratory drilling costs by 60–80%', 'Receive prioritized high-potential zone maps']} />
            <UseCaseCard icon="🛢️" title="Oil & Gas" points={['Identify surface hydrocarbon seepage signatures', 'Map geological fault structures and trap formations', 'Cut prospecting timelines from months to days']} />
            <UseCaseCard icon="🌾" title="Agriculture & Crop Intelligence" points={['Predict harvest yields weeks before harvest', 'Detect water stress and disease signatures early', 'Optimize inputs (water, fertilizer) zone by zone']} />
            <UseCaseCard icon="🏗️" title="Real Estate & Infrastructure" points={['Analyze soil stability and subsurface risk', 'Identify groundwater levels and flood hazard zones', 'Reduce geotechnical survey costs']} />
            <UseCaseCard icon="🌍" title="Land & Territory Management" points={['Monitor illegal extraction or encroachment', 'Map vegetation, erosion, and environmental changes', 'Support government and NGO decision-making']} />
          </div>
        </div>
      </section>

      {/* ─── STATS & SOCIAL PROOF ─── */}
      <section className="py-20 md:py-28" style={{ background: 'linear-gradient(135deg, #1B3A6B, #0f2847)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {[
              { end: 3200, suffix: '+', label: 'Zones Analyzed', icon: '🛰️' },
              { end: 47, suffix: '', label: 'Countries Covered', icon: '🌍' },
              { end: 48, suffix: 'H', label: 'Average Delivery', icon: '⏱️' },
              { end: 98, suffix: '%', label: 'Client Satisfaction', icon: '📊' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl mb-2">{s.icon}</div>
                <AnimatedCounter end={s.end} suffix={s.suffix} />
                <div className="text-white/60 text-sm mt-2">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: 'SOCRAT identified a mineralization zone our team had overlooked for 3 years. The report was on my desk in 36 hours.', author: 'Mining Director, West Africa' },
              { quote: 'We validated a 12,000 hectare agricultural zone before acquisition. The yield prediction was within 4% of actual harvest.', author: 'Investment Fund Manager, Morocco' },
              { quote: 'For our oil exploration permit due diligence, Sovereign Scout delivered what would have taken our geologists 4 months — in 48 hours.', author: 'E&P Director, Gulf Region' },
            ].map((t, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <p className="text-white/80 italic mb-4 leading-relaxed">"{t.quote}"</p>
                <p className="text-sovereign-gold text-sm font-semibold">— {t.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section id="pricing" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-sovereign-navy text-center mb-4">
            Intelligence <span className="text-sovereign-gold">Reports</span>
          </h2>
          <p className="text-center text-sovereign-navy/60 mb-16 max-w-2xl mx-auto">Choose the depth of analysis that matches your decision stage.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <PricingCard title="SCAN SIMPLE" desc="Zone overview to validate initial intuition" features={['Spectral anomaly map', '2-page summary', 'PDF format', '24–48H delivery']} cta="Request Scan →" />
            <PricingCard title="INTELLIGENCE DOSSIER" desc="Full audit for investors & directors" features={['Multi-layer analysis', 'Risk index mapping', '15+ page executive report', 'Historical comparison', 'NDA included', '5–7 day delivery']} cta="Request Dossier →" highlighted />
            <PricingCard title="LAB MODE SCAN" desc="Maximum precision — integrates your own data" features={['Satellite + drone fusion', 'Historical document integration', 'Drilling report overlay', '3D anomaly visualization', '2–4 week delivery']} cta="Request Lab Scan →" />
            <PricingCard title="SOVEREIGN PLATFORM" desc="Private dashboard for ongoing intelligence" features={['SOCRAT AI agent', 'Real-time satellite feeds', 'Custom alerts', 'API access', 'Team collaboration', 'Annual subscription']} cta="Book Demo →" />
          </div>
        </div>
      </section>

      {/* ─── LEAD FORM ─── */}
      <section id="analyze-my-zone" className="py-20 md:py-28" style={{ background: 'linear-gradient(135deg, #1B3A6B, #0f2847)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
            {submitted ? (
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center py-8">
                <div className="text-6xl mb-4">✅</div>
                <h3 className="text-2xl font-bold text-sovereign-navy mb-4">Request Received!</h3>
                <p className="text-sovereign-navy/70 max-w-md mx-auto">Our intelligence team will contact you within 4 hours to confirm your zone analysis. A confirmation has been sent to <strong>{formEmail}</strong>.</p>
              </motion.div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-extrabold text-sovereign-navy mb-2">
                    🛰️ Analyze My Zone in 48 Hours
                  </h2>
                  <p className="text-sovereign-navy/60">Fill in the form below. Confidential. NDA available.</p>
                </div>
                {/* Progress bar */}
                <div className="flex gap-2 mb-8">
                  <div className={`h-1.5 rounded-full flex-1 transition-colors ${formStep >= 1 ? 'bg-sovereign-gold' : 'bg-gray-200'}`} />
                  <div className={`h-1.5 rounded-full flex-1 transition-colors ${formStep >= 2 ? 'bg-sovereign-gold' : 'bg-gray-200'}`} />
                </div>

                <form onSubmit={handleSubmit}>
                  {formStep === 1 && (
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
                      <h3 className="font-bold text-sovereign-navy text-sm uppercase tracking-wider mb-4">Step 1 — Zone & Contact Info</h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-sovereign-navy mb-1">Full Name *</label>
                          <input type="text" required value={form.full_name} onChange={e => updateField('full_name', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-sovereign-navy mb-1">Company</label>
                          <input type="text" value={form.company} onChange={e => updateField('company', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all" />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-sovereign-navy mb-1">Professional Email *</label>
                          <input type="email" required value={form.email} onChange={e => updateField('email', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-sovereign-navy mb-1">Phone / WhatsApp</label>
                          <input type="tel" value={form.phone} onChange={e => updateField('phone', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-sovereign-navy mb-1">Country *</label>
                        <select required value={form.country} onChange={e => updateField('country', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all">
                          <option value="">Select country...</option>
                          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-sovereign-navy mb-1">Zone Location Description *</label>
                        <textarea required value={form.zone_location} onChange={e => updateField('zone_location', e.target.value)} placeholder="e.g. Northern Mali, 20km east of Kidal — or paste GPS coordinates" rows={3} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all resize-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-sovereign-navy mb-2">Approximate Zone Size *</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {['< 50 km²', '50–500 km²', '500–5,000 km²', '> 5,000 km²'].map(size => (
                            <label key={size} className={`cursor-pointer border-2 rounded-lg px-3 py-2.5 text-center text-sm font-medium transition-all ${form.zone_size === size ? 'border-sovereign-gold bg-sovereign-gold/10 text-sovereign-navy' : 'border-gray-200 text-sovereign-navy/60 hover:border-sovereign-gold/50'}`}>
                              <input type="radio" name="zone_size" value={size} checked={form.zone_size === size} onChange={e => updateField('zone_size', e.target.value)} className="sr-only" />
                              {size}
                            </label>
                          ))}
                        </div>
                      </div>
                      <button type="button" disabled={!step1Valid} onClick={() => setFormStep(2)} className="w-full bg-sovereign-navy text-white py-4 rounded-lg font-bold text-lg border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                        Next Step <ArrowRight className="h-5 w-5" />
                      </button>
                    </motion.div>
                  )}

                  {formStep === 2 && (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
                      <h3 className="font-bold text-sovereign-navy text-sm uppercase tracking-wider mb-4">Step 2 — Analysis Type & Context</h3>
                      <div>
                        <label className="block text-sm font-medium text-sovereign-navy mb-2">Type of Analysis Needed *</label>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {['Mining & Mineral Exploration', 'Oil & Gas Prospecting', 'Agricultural Yield & Crop Intelligence', 'Real Estate / Infrastructure Soil Analysis', 'Environmental / Land Monitoring', 'Water Resource Detection'].map(type => (
                            <label key={type} className={`cursor-pointer flex items-center gap-2 border-2 rounded-lg px-3 py-2.5 text-sm transition-all ${form.analysis_types.includes(type) ? 'border-sovereign-gold bg-sovereign-gold/10 text-sovereign-navy font-medium' : 'border-gray-200 text-sovereign-navy/60 hover:border-sovereign-gold/50'}`}>
                              <input type="checkbox" checked={form.analysis_types.includes(type)} onChange={() => toggleAnalysisType(type)} className="sr-only" />
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${form.analysis_types.includes(type) ? 'border-sovereign-gold bg-sovereign-gold' : 'border-gray-300'}`}>
                                {form.analysis_types.includes(type) && <Check className="h-3 w-3 text-white" />}
                              </div>
                              {type}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-sovereign-navy mb-2">Report Type *</label>
                        <div className="space-y-2">
                          {[
                            { value: 'Simple Scan (24–48H)', label: 'Simple Scan (24–48H)' },
                            { value: 'Intelligence Dossier (5–7 days)', label: 'Intelligence Dossier (5–7 days)' },
                            { value: 'Lab Mode Scan (2–4 weeks)', label: 'Lab Mode Scan (2–4 weeks)' },
                            { value: 'Platform Demo', label: 'Platform Demo' },
                          ].map(opt => (
                            <label key={opt.value} className={`cursor-pointer flex items-center gap-3 border-2 rounded-lg px-4 py-3 text-sm transition-all ${form.report_type === opt.value ? 'border-sovereign-gold bg-sovereign-gold/10 text-sovereign-navy font-medium' : 'border-gray-200 text-sovereign-navy/60 hover:border-sovereign-gold/50'}`}>
                              <input type="radio" name="report_type" value={opt.value} checked={form.report_type === opt.value} onChange={e => updateField('report_type', e.target.value)} className="sr-only" />
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${form.report_type === opt.value ? 'border-sovereign-gold' : 'border-gray-300'}`}>
                                {form.report_type === opt.value && <div className="w-2 h-2 rounded-full bg-sovereign-gold" />}
                              </div>
                              {opt.label}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-sovereign-navy mb-1">Project Urgency</label>
                        <select value={form.project_urgency} onChange={e => updateField('project_urgency', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all">
                          <option value="standard">Standard</option>
                          <option value="urgent">Urgent (&lt; 48H)</option>
                          <option value="critical">Critical — Board Decision Imminent</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-sovereign-navy mb-1">Additional Context</label>
                        <textarea value={form.additional_context} onChange={e => updateField('additional_context', e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all resize-none" />
                      </div>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" checked={form.nda_required} onChange={e => updateField('nda_required', e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-sovereign-gold focus:ring-sovereign-gold" />
                        <span className="text-sm text-sovereign-navy">Yes, I require a Non-Disclosure Agreement</span>
                      </label>
                      <div>
                        <label className="block text-sm font-medium text-sovereign-navy mb-1">How did you find us?</label>
                        <select value={form.how_found_us} onChange={e => updateField('how_found_us', e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sovereign-navy focus:ring-2 focus:ring-sovereign-gold focus:border-sovereign-gold outline-none transition-all">
                          <option value="">Select...</option>
                          <option value="Google">Google</option>
                          <option value="LinkedIn">LinkedIn</option>
                          <option value="Referral">Referral</option>
                          <option value="Conference">Conference</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="flex gap-4">
                        <button type="button" onClick={() => setFormStep(1)} className="flex-1 border-2 border-sovereign-navy text-sovereign-navy py-4 rounded-lg font-semibold hover:bg-sovereign-navy/5 transition-all">
                          ← Back
                        </button>
                        <button type="submit" disabled={!step2Valid || submitting} className="flex-1 bg-sovereign-navy text-white py-4 rounded-lg font-bold text-lg border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                          {submitting ? 'Submitting...' : 'Submit Analysis Request →'}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-sovereign-navy text-center mb-16">
            Frequently Asked <span className="text-sovereign-gold">Questions</span>
          </h2>
          <div>
            <FaqItem q="What is satellite natural resource exploration and how does SOCRAT use it?" a="Satellite natural resource exploration uses multi-spectral and thermal imaging from orbital satellites to detect geological anomalies, mineral signatures, and hydrocarbon seepage patterns on Earth's surface. SOCRAT, our AI-powered platform, processes data from multiple satellite constellations using deep-learning models to generate precise intelligence reports identifying high-potential zones for mining, oil & gas, and agricultural operations — all within 48 hours." />
            <FaqItem q="How accurate are your satellite mineral exploration reports?" a="Our reports achieve 85–95% confidence levels depending on the analysis type and zone conditions. We use multi-spectral imaging at resolutions down to 30cm combined with thermal and SAR data. Each report includes a confidence index and methodology transparency so you can make informed investment decisions." />
            <FaqItem q="Can satellite analysis really replace geological field surveys?" a="SOCRAT is designed as a powerful force multiplier, not a replacement. By pre-screening zones from space, you can focus expensive field surveys only on the highest-potential areas — reducing exploration costs by 60–80% and cutting timelines from months to days." />
            <FaqItem q="What types of natural resources can you detect from satellite?" a="Our platform detects signatures for gold, copper, iron, lithium, rare earth elements, oil and gas seepage, groundwater, soil composition, crop health, and vegetation patterns. Each resource type uses specialized spectral bands and AI models trained on verified geological datasets." />
            <FaqItem q="How is Sovereign Scout different from Google Earth or standard GIS tools?" a="Google Earth provides visual photography. Sovereign Scout delivers physics-level material analysis — we analyze the spectral signatures of materials, thermal emissions, and geological structures invisible to standard imagery. Plus, you receive executive-ready PDF reports in 48 hours, not raw data requiring GIS expertise." />
            <FaqItem q="Is my zone data kept confidential?" a="Absolutely. All zone data is encrypted in transit and at rest. We offer NDA agreements by default for all Intelligence Dossier and Lab Mode analyses. Your data is stored in segmented cloud infrastructure with zero cross-client access." />
            <FaqItem q="What do I need to submit to get my analysis started?" a="Just your zone location (coordinates, place name, or description) and the type of analysis you need. No technical knowledge required. Our online form takes less than 3 minutes to complete." />
            <FaqItem q="Do you cover all countries and regions?" a="Yes — SOCRAT provides global satellite coverage. We have particular expertise in Africa, the Middle East, Latin America, and Central Asia, but our constellation covers every point on Earth. Whether it's a 50 km² scan or a 5,000 km² territory, we scale with your needs." />
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-16" style={{ background: '#1B3A6B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Satellite className="h-6 w-6 text-sovereign-gold" />
                <span className="font-extrabold text-white tracking-widest">SOVEREIGN SCOUT</span>
              </div>
              <p className="text-white/50 text-sm">Your Territory. Decoded from Space.</p>
            </div>
            <div>
              <h4 className="text-sovereign-gold font-bold text-sm mb-4">Solutions</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">Mining</button></li>
                <li><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">Oil & Gas</button></li>
                <li><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">Agriculture</button></li>
                <li><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">Real Estate</button></li>
                <li><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">Land Management</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sovereign-gold font-bold text-sm mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><span className="hover:text-white transition-colors cursor-pointer">About</span></li>
                <li><span className="hover:text-white transition-colors cursor-pointer">Technology</span></li>
                <li><span className="hover:text-white transition-colors cursor-pointer">Case Studies</span></li>
                <li><span className="hover:text-white transition-colors cursor-pointer">Blog</span></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sovereign-gold font-bold text-sm mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li className="flex items-center gap-2"><Mail className="h-4 w-4" /> admin@quantumbox.ai</li>
                <li className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Paris, France</li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a href="https://www.linkedin.com/company/quantumbox-ia/" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-sovereign-gold transition-colors"><Linkedin className="h-5 w-5" /></a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-xs">© 2025 Sovereign Scout. All rights reserved.</p>
            <div className="flex gap-6 text-xs text-white/40">
              <span className="hover:text-white cursor-pointer">Privacy Policy</span>
              <span className="hover:text-white cursor-pointer">Terms</span>
              <span className="hover:text-white cursor-pointer">NDA Request</span>
            </div>
          </div>
          <p className="text-white/30 text-xs mt-4 text-center">Sovereign Scout is an independent geospatial intelligence service. All analyses are produced using multi-source satellite data and proprietary AI models.</p>
        </div>
      </footer>

      {/* ─── SAMPLE REPORT MODAL ─── */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowModal(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative" onClick={e => e.stopPropagation()}>
              <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-6xl font-extrabold text-sovereign-navy/5 rotate-[-30deg]">CONFIDENTIAL</span>
                </div>
                <div className="border-b border-gray-200 pb-6 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Satellite className="h-5 w-5 text-sovereign-navy" />
                    <span className="font-extrabold text-sovereign-navy tracking-wider text-sm">SOVEREIGN SCOUT</span>
                  </div>
                  <h3 className="text-xl font-bold text-sovereign-navy">Intelligence Report — Zone Analysis</h3>
                  <p className="text-sovereign-gold font-medium">West African Mining Corridor</p>
                  <div className="flex gap-6 mt-3 text-xs text-sovereign-navy/50">
                    <span>Client: [Redacted]</span>
                    <span>Date: March 2025</span>
                    <span>Type: Mineral Exploration</span>
                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold text-sovereign-navy mb-2">1. Executive Summary</h4>
                    <p className="text-sovereign-navy/70 text-sm">Multi-spectral analysis reveals three high-confidence mineralization zones within the target corridor, with gold and copper anomaly signatures...</p>
                    <div className="h-12 bg-gradient-to-b from-transparent to-gray-100 rounded-lg mt-2" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sovereign-navy mb-2">2. Anomaly Detection Map</h4>
                    <div className="bg-gray-100 rounded-lg h-40 flex items-center justify-center border-2 border-dashed border-gray-300">
                      <span className="text-gray-400 font-bold tracking-widest">MAP CLASSIFIED</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-sovereign-navy mb-2">3. High-Priority Zones Identified</h4>
                    <p className="text-sovereign-navy/70 text-sm">3 zones flagged for immediate ground-truthing with confidence levels of 92%, 87%, and 84%...</p>
                    <div className="h-12 bg-gradient-to-b from-transparent to-gray-100 rounded-lg mt-2" />
                  </div>
                </div>
                <div className="mt-8 text-center">
                  <button onClick={() => { setShowModal(false); setTimeout(() => scrollTo('analyze-my-zone'), 300); }} className="bg-sovereign-navy text-white px-8 py-4 rounded-lg font-bold border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy transition-all inline-flex items-center gap-2">
                    Get Your Own Report <ArrowRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SovereignScout;
